// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { supabase } from '../supabaseClient';


const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
            setSession(initialSession);
            setLoading(false);
        }).catch((error) => {
            console.error("Error getting initial session:", error);
            setLoading(false);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (_event, currentSession) => {
                console.log("Auth State Change Event (AuthProvider):", _event, "New Session:", currentSession);
                setSession(currentSession);
                if (loading && (_event === 'SIGNED_IN' || _event === 'SIGNED_OUT' || _event === 'INITIAL_SESSION' || _event === 'TOKEN_REFRESHED' || _event === 'USER_UPDATED')) {
                    setLoading(false);
                }
            }
        );

        return () => {
            subscription?.unsubscribe();
        };
    }, []);

    const signUp = async ({ email, password, options }) =>
        await supabase.auth.signUp({ email, password, options });

    const signInWithPassword = async ({ email, password }) =>
        await supabase.auth.signInWithPassword({ email, password });

    const signInWithGoogle = async () => 
        await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: window.location.origin + '/main-menu' }
        });

    const signOut = async () => await supabase.auth.signOut();

    const resetPassword = async (email) => {
        const resetRedirectUrl = `${window.location.origin}/update-password`;
        console.log(`Requesting password reset for ${email} with redirect to ${resetRedirectUrl}`);
        return await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: resetRedirectUrl,
        });
    };

    const updateUser = async (attributes) => {
        const { data, error } = await supabase.auth.updateUser(attributes);
        if (error) console.error("Error updating user:", error.message);
        return { data, error };
    };

    // --- Fetch extended user profile from `profiles` ---
    const fetchUserProfile = async () => {
        if (!session?.user?.id) return null;
        const { data, error } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', session.user.id)
            .single();
        if (error) {
            console.error("Error fetching profile:", error.message);
            return null;
        }
        return data;
    };

    // --- Update profile data in `profiles` ---
    const updateProfileData = async (updates) => {
        if (!session?.user?.id) return null;
        const { data, error } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', session.user.id);
        if (error) {
            console.error("Error updating profile:", error.message);
            return null;
        }
        return data;
    };

    // --- Upload avatar to Supabase Storage ---
    const uploadAvatar = async (file) => {
        if (!file || file.size > 1024 * 1024) { // 1MB limit
            throw new Error('File must be under 1MB.');
        }

        const fileExt = file.name.split('.').pop();
        const filePath = `${session.user.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase
            .storage
            .from('avatars')
            .upload(filePath, file);

        if (uploadError) {
            console.error("Error uploading avatar:", uploadError.message);
            throw uploadError;
        }

        const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
        return data.publicUrl || filePath;
    };

    const value = useMemo(() => ({
      session,
      user: session?.user ?? null,
      loading,
      signUp,
      signInWithPassword,
      signInWithGoogle,
      signOut,
      resetPassword,
      updateUser,
      fetchUserProfile, // Make sure this is included
      updateProfileData,
      uploadAvatar
  }), [session, loading]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
